import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";

import Toast from '../../components/Toast/Toast';
import { addProductAPI, getCategoriesAPI, getBrandsAPI } from '../../utils/api';
import Input from '../../components/Input/Input';
import TextArea from '../../components/Input/TextArea';
import Select from '../../components/Input/Select';
import Checkbox from '../../components/Input/Checkbox';
import "./AddProduct.css";
import { FaArrowLeft } from "react-icons/fa";
import RichTextInput from '../../components/Input/RichTextInput';

const AddProduct = () => {
  const { catId } = useParams();
  const navigate = useNavigate();

  const [productName, setProductName] = useState("");
  const [sku, setSku] = useState("");
  const [description, setDescription] = useState("");
  const [featured, setFeatured] = useState(false);
  const [onSale, setOnSale] = useState(false);
  const [returnPolicy, setReturnPolicy] = useState("");
  const [fabric, setFabric] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [BrandId, setBrandId] = useState("");
  const [colors, setColors] = useState([
    { name: "No Color", hex_code: "rgba(0, 0, 0, 0)", isNoColor: true }
  ]);
  const [sizes, setSizes] = useState([{ size: "", wholesale_price: "", retail_price: "", mrp: "", unit: "" }]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingBrands, setLoadingBrands] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [video, setVideo] = useState(null);
  const [videoThumbnail, setVideoThumbnail] = useState(null);

  const showToast = (message, type = "error") => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: "", type: "" }), 2500);
  };


  // Fetch categories from API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoadingCategories(true);
        const response = await getCategoriesAPI();

        if (response?.status === 200) {
          setCategories(response.data || []);
        } else if (response?.message) {
          console?.error('Error loading categories:', typeof response?.message === 'string'
            ? response?.message
            : 'Failed due to internal error');
        } else {
          console?.error('Failed due to internal error');
        }
      } catch (err) {
        console?.error('Failed due to internal error');
      } finally {
        setLoadingCategories(false);
      }
    };

    fetchCategories();
  }, []);

  // Fetch brands from API
  useEffect(() => {
    const fetchBrands = async () => {
      try {
        setLoadingBrands(true);
        const response = await getBrandsAPI();

        if (response?.status === 200) {
          setBrands(response.data || []);
        } else if (response?.message) {
          console.error('Error loading brands:', typeof response?.message === 'string'
            ? response?.message
            : 'Failed due to internal error');
        } else {
          console.error('Failed due to internal error');
        }
      } catch (err) {
        console.error('Failed due to internal error');
      } finally {
        setLoadingBrands(false);
      }
    };

    fetchBrands();
  }, []);

  // Handle input change for color name or hex
  const handleColorChange = (idx, field, value) => {
    const updated = colors.map((color, i) =>
      i === idx ? { ...color, [field]: value } : color
    );
    setColors(updated);
  };

  // Add a new color input row
  const handleAddMoreColor = () => {
    setColors([...colors, { name: "", hex_code: "rgb(0, 0, 0)" }]);
  };

  const handleRemoveColor = (idx) => {
    const colorToDelete = colors[idx];
    const newColors = colors.filter((_, i) => i !== idx);
    setColors(newColors);
    showToast(`Color "${colorToDelete.name}" deleted successfully.`, "success");
  };

  const handleSizeChange = (idx, field, value) => {
    // Only allow numbers for wholesale_price, retail_price,mrp and unit
    if (["wholesale_price", "retail_price", "mrp", "unit"].includes(field)) {
      if (!/^[0-9]*$/.test(value)) return;
    }
    const updated = sizes.map((row, i) =>
      i === idx ? { ...row, [field]: value } : row
    );
    setSizes(updated);
  };

  const handleAddSizeRow = () => {
    // Get the last size object or use empty values if no sizes exist yet
    const lastSize = sizes.length > 0 ? sizes[sizes.length - 1] : null;

    setSizes([...sizes, {
      size: "",
      wholesale_price: lastSize?.wholesale_price || "",
      retail_price: lastSize?.retail_price || "",
      mrp: lastSize?.mrp || "",
      unit: lastSize?.unit || ""
    }]);
  };

  const handleRemoveSizeRow = (idx) => {
    setSizes(sizes.filter((_, i) => i !== idx));
  };

  // Convert file to base64
  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader?.result);
      reader.onerror = error => reject(error);
    });
  };

  // Handle video thumbnail upload
  const handleVideoThumbnailChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpg', 'image/jpeg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      showToast("Invalid file format. Only PNG, JPG, JPEG, and WebP files are allowed for thumbnail.", "error");
      e.target.value = '';
      return;
    }

    // Validate file size (max 20MB)
    const maxSize = 40 * 1024 * 1024; // 40MB in bytes
    if (file.size > maxSize) {
      showToast(`File too large: ${file.name}. Maximum file size is 40MB.`, "error");
      e.target.value = '';
      return;
    }

    // Store file directly like SignUp component
    setVideoThumbnail(file);
  };

  // Handle video upload
  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type - only MP4, WebM, and MOV
    const allowedTypes = ['video/mp4', 'video/webm', 'video/quicktime'];
    if (!allowedTypes.includes(file.type)) {
      showToast("Invalid file format. Only MP4, WebM and MOV files are allowed for video.", "error");
      e.target.value = '';
      return;
    }

    // Validate file size (max 20MB)
    const maxSize = 40 * 1024 * 1024; // 40MB in bytes
    if (file.size > maxSize) {
      showToast(`File too large: ${file.name}. Maximum file size is 40MB.`, "error");
      e.target.value = '';
      return;
    }

    // Store file directly like SignUp component
    setVideo(file);
  };

  // Remove video thumbnail
  const handleRemoveVideoThumbnail = () => {
    setVideoThumbnail(null);
    // Reset thumbnail input
    const thumbnailInput = document.getElementById('video-thumbnail-input');
    if (thumbnailInput) thumbnailInput.value = '';
  };

  // Remove video
  const handleRemoveVideo = () => {
    setVideo(null);
    // Reset video input
    const videoInput = document.getElementById('video-upload-input');
    if (videoInput) videoInput.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setSubmitLoading(true);

    // Validation checks
    if (!productName.trim()) {
      showToast("Please enter the product name.");
      setSubmitLoading(false);
      return;
    }
    if (!categoryId) {
      showToast("Please select a category.");
      setSubmitLoading(false);
      return;
    }

    // Size validation - check for incomplete size entries
    const incompleteSizes = sizes.filter(row => {
      const hasSize = row.size?.trim();
      const hasWholesale = row.wholesale_price?.toString().trim();
      const hasRetail = row.retail_price?.toString().trim();
      const hasUnit = row.unit?.toString().trim();

      // Check if any field is filled but not all
      const filledFields = [hasSize, hasWholesale, hasRetail, hasUnit].filter(Boolean).length;
      return filledFields > 0 && filledFields < 4;
    });

    if (incompleteSizes.length > 0) {
      // Show common message for incomplete sizes
      showToast("Please complete all size fields");
      setSubmitLoading(false);
      return;
    }

    if (!sizes?.some(row => row.size && row.wholesale_price && row?.retail_price && row?.unit)) {
      showToast("Please add at least one size.");
      setSubmitLoading(false);
      return;
    }

    // Brand is optional - use default brand if not selected
    const selectedBrandId = BrandId || (brands.length > 0 ? brands[0]._id : null);
    if (!selectedBrandId) {
      showToast("No brands available. Please add a brand first.");
      setSubmitLoading(false);
      return;
    }

    // Color validation - check for incomplete color entries
    const colorsWithNameButNoHex = colors.filter(c => c.name?.trim() && !c.hex_code);
    const colorsWithHexButNoName = colors.filter(c => c.hex_code && !c.name?.trim());

    if (colorsWithNameButNoHex.length > 0) {
      const colorNames = colorsWithNameButNoHex.map(c => c.name).join(', ');
      showToast(`Please enter hex code for color ${colorNames}`);
      setSubmitLoading(false);
      return;
    }

    if (colorsWithHexButNoName.length > 0) {
      const hexCodes = colorsWithHexButNoName.map(c => c.hex_code).join(', ');
      showToast(`Please enter color name for hex code ${hexCodes}`);
      setSubmitLoading(false);
      return;
    }

    // Check if at least one complete color is added
    if (!colors.some(c => c.name && c.hex_code)) {
      showToast("Please add at least one color with name and hex code.");
      setSubmitLoading(false);
      return;
    }

    // Video and thumbnail validation - if one is added, both must be added
    if (video && !videoThumbnail) {
      showToast("Please add a video thumbnail.");
      setSubmitLoading(false);
      return;
    }

    if (videoThumbnail && !video) {
      showToast("Please add a video.");
      setSubmitLoading(false);
      return;
    }

    try {
      const formData = new FormData();

      formData.append('name', productName);
      formData.append('sku', sku);
      formData.append('description', description);
      formData.append('is_featured', featured ? 1 : 0);
      formData.append('on_sale', onSale ? 1 : 0);
      formData.append('return_policy', returnPolicy);
      formData.append('fabric', fabric);
      formData.append('category_id', parseInt(categoryId));
      formData.append('brand_id', parseInt(selectedBrandId));

      // Append colors as array
      const colorsData = colors.filter(c => c.name && c.hex_code);
      if (colorsData.length > 0) {
        colorsData.forEach((color) => {
          formData.append('colors[]', JSON.stringify({
            name: color.name,
            hex_code: color.hex_code
          }));
        });
      }

      // Append sizes as array
      const sizesData = sizes.filter(row => row.size && row.wholesale_price && row.retail_price && row.unit);
      if (sizesData.length > 0) {
        sizesData.forEach((row) => {
          formData.append('sizes[]', JSON.stringify({
            size: row.size,
            retail_price: parseInt(row.retail_price),
            wholesale_price: parseInt(row.wholesale_price),
            unit: parseInt(row.unit),
            mrp: parseInt(row.mrp)
          }));
        });
      }

      // Append video thumbnail as base64 (if provided) - convert file to base64 when submitting
      if (videoThumbnail) {
        try {
          const thumbnailBase64 = await convertToBase64(videoThumbnail);
          formData.append('video_thumbnail', thumbnailBase64);
        } catch (error) {
          console.error('Error converting video thumbnail to base64:', error);
          showToast("Failed to process video thumbnail.", "error");
          setSubmitLoading(false);
          return;
        }
      }

      // Append video as file (if provided) - send file directly like SignUp component
      if (video) {
        formData.append('video', video);
      }

      // console.log('Product Data (FormData):', {
      //   name: productName,
      //   sku: sku,
      //   description: description,
      //   is_featured: featured ? 1 : 0,
      //   on_sale: onSale ? 1 : 0,
      //   return_policy: returnPolicy,
      //   fabric: fabric,
      //   category_id: parseInt(categoryId),
      //   brand_id: parseInt(selectedBrandId),
      //   colors: colorsData,
      //   sizes: sizesData
      // });

      const response = await addProductAPI(formData);

      if (response?.status === 200) {
        setProductName("");
        setSku("");
        setDescription("");
        setFeatured(false);
        setOnSale(false);
        setReturnPolicy("");
        setFabric("");
        setCategoryId("");
        setBrandId("");
        setColors([{ name: "No Color", hex_code: "rgba(0, 0, 0, 0)", isNoColor: true }]);
        setSizes([{ size: "", wholesale_price: "", retail_price: "", mrp: "", unit: "" }]);
        setVideo(null);
        setVideoThumbnail(null);

        showToast("Product created successfully.", "success");
        const url = catId
          ? `/product?categoryId=${catId}&refresh=true`
          : `/product?refresh=true`;
        navigate(url);
      } else if (response?.message) {
        showToast(typeof response?.message === 'string'
          ? response?.message
          : 'Failed due to internal error', "error");
      } else {
        showToast('Failed due to internal error', "error");
      }
    } catch (err) {
      showToast('Failed due to internal error', "error");
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <>
      <Toast toast={toast} />
      <div className="product-main-content">
        <div className="order-details-header">
          <button className="back-button" onClick={() => {
            const url = catId
              ? `/product?categoryId=${catId}`
              : `/product`;
            navigate(url);
          }}>
            <FaArrowLeft /> Back to Products
          </button>
        </div>
        <form onSubmit={handleSubmit} className="product-form">
          <TextArea
            title="Product Name"
            value={productName}
            onChange={e => setProductName(e.target.value)}
            placeholder="Enter product name"
            required
            maxLength={200}
            rows={3}
          />
          <Input
            title="SKU"
            value={sku}
            onChange={e => setSku(e.target.value)}
            placeholder="Enter SKU"
            required
            maxLength={100}
          />

          <RichTextInput
            title="Description"
            value={description}
            onChange={setDescription}
            placeholder="Enter product description"
            rows={6}
          />

          <RichTextInput
            title="Return Policy"
            value={returnPolicy}
            onChange={setReturnPolicy}
            placeholder="Enter return policy..."
            rows={6}
          />

          <Input
            title="Fabric"
            type="text"
            value={fabric}
            onChange={e => setFabric(e.target.value)}
            placeholder="Enter fabric type (e.g. Cotton, Silk)"
            maxLength={200}
          />

          <Select
            title="Category"
            value={categoryId}
            onChange={e => setCategoryId(e.target.value)}
            placeholder={loadingCategories ? "Loading categories..." : "Select category"}
            required
            disabled={loadingCategories}
            options={categories.map(cat => ({ value: cat._id, label: cat.name }))}
          />

          <Select
            title="Brand"
            value={BrandId}
            onChange={e => setBrandId(e.target.value)}
            placeholder={loadingBrands ? "Loading brands..." : "Select Brand"}
            disabled={loadingBrands}
            options={brands.map(brand => ({ value: brand._id, label: brand.name }))}
          />

          <Checkbox
            title="Featured"
            checked={featured}
            onChange={e => setFeatured(e.target.checked)}
            className="product-checkbox-container"
          />
          <Checkbox
            title="Sale"
            checked={onSale}
            onChange={e => setOnSale(e.target.checked)}
          />

          <div className="size-section">
            <h3 className="size-title">Size</h3>
            <div className="size-header">
              <div className="size-header-item">SIZE</div>
              <div className="size-header-item">WHOLESALE PRICE</div>
              <div className="size-header-item">RETAIL PRICE</div>
              <div className="size-header-item">MRP</div>
              <div className="size-header-item">UNIT</div>
              <div className="remove-price-header-placeholder"></div>
            </div>
            {sizes.map((row, idx) => (
              <div className="price-row" key={idx}>
                <input
                  type="text"
                  className="size-textarea"
                  placeholder="Size"
                  value={row.size}
                  onChange={e => handleSizeChange(idx, "size", e.target.value)}
                />
                <input
                  type="text"
                  className="price-input"
                  placeholder="Wholesale Price"
                  value={row.wholesale_price}
                  onChange={e => handleSizeChange(idx, "wholesale_price", e.target.value)}
                  maxLength={8}
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
                <input
                  type="text"
                  className="price-input"
                  placeholder="Retail Price"
                  value={row.retail_price}
                  onChange={e => handleSizeChange(idx, "retail_price", e.target.value)}
                  maxLength={8}
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
                <input
                  type="text"
                  className="price-input"
                  placeholder="MRP"
                  value={row.mrp}
                  onChange={e => handleSizeChange(idx, "mrp", e.target.value)}
                  maxLength={8}
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
                <input
                  type="text"
                  className="price-input"
                  placeholder="Unit"
                  value={row.unit}
                  onChange={e => handleSizeChange(idx, "unit", e.target.value)}
                  maxLength={8}
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
                {sizes.length > 1 ? (
                  <button
                    type="button"
                    className="remove-price-btn"
                    onClick={() => handleRemoveSizeRow(idx)}
                    title="Delete"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                      strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6"></polyline>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"></path>
                      <line x1="10" y1="11" x2="10" y2="17"></line>
                      <line x1="14" y1="11" x2="14" y2="17"></line>
                    </svg>
                  </button>
                ) : (
                  <div className="remove-price-header-placeholder"></div>
                )}
              </div>
            ))}
            <div className="add-price-btn-container">
              <button
                type="button"
                className="add-price-btn"
                onClick={handleAddSizeRow}
                title="Add Size"
              >
                Add More
              </button>
            </div>
          </div>

          {/* Color Section */}
          <div className="size-section">
            <h3 className="size-title">Color</h3>
            <div className="size-header">
              <div className="size-header-item">COLOR NAME</div>
              <div className="size-header-item">COLOR PICKER</div>
              <div className="size-header-item">HEX CODE</div>
              <div className="remove-size-header-placeholder"></div>
            </div>
            {colors.map((color, idx) => (
              <div className="price-row" key={idx}>
                <input
                  type="text"
                  placeholder="Color name"
                  value={color.name}
                  onChange={e => handleColorChange(idx, "name", e.target.value)}
                  className="size-textarea"
                  disabled={color.isNoColor}
                />
                <input
                  type="color"
                  value={color.hex_code === "rgba(0, 0, 0, 0)" ? "#000000" : color.hex_code || "#000000"}
                  onChange={e => handleColorChange(idx, "hex_code", e.target.value)}
                  className="color-picker-input"
                  disabled={color.isNoColor}
                />
                <input
                  type="text"
                  placeholder="Hex code"
                  value={color.hex_code}
                  onChange={e => handleColorChange(idx, "hex_code", e.target.value)}
                  className="size-textarea"
                  maxLength={7}
                  disabled={color.isNoColor}
                />
                {!color.isNoColor && colors.length > 1 ? (
                  <button
                    type="button"
                    className="remove-price-btn"
                    onClick={() => handleRemoveColor(idx)}
                    title="Delete"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                      strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6"></polyline>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"></path>
                      <line x1="10" y1="11" x2="10" y2="17"></line>
                      <line x1="14" y1="11" x2="14" y2="17"></line>
                    </svg>
                  </button>
                ) : (
                  <div className="remove-color-btn-placeholder"></div>
                )}
              </div>
            ))}
            <div className="add-size-btn-container">
              <button
                type="button"
                onClick={handleAddMoreColor}
                className="add-price-btn"
                title="Add Color"
              >
                Add More
              </button>
            </div>
          </div>

          {/* Video Section */}
          <div className="size-section">
            <h3 className="size-title" style={{ textAlign: 'left' }}>Add Video</h3>
            <div className="video-section-container" style={{ display: 'flex', gap: '2rem', alignItems: 'flex-start' }}>
              {/* Video Thumbnail */}
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.75rem',
                  fontWeight: 700,
                  color: '#475569',
                  fontSize: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  textAlign: 'left',
                }}>
                  Thumbnail
                </label>
                <div className="image-boxes-container" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  {videoThumbnail && (
                    <div className="image-preview-item">
                      <img
                        src={URL.createObjectURL(videoThumbnail)}
                        alt="video-thumbnail"
                        className="image-preview"
                        style={{
                          width: '200px',
                          height: '150px',
                          objectFit: 'cover',
                          borderRadius: '12px',
                          border: '2px solid #e2e8f0'
                        }}
                      />
                      <button
                        type="button"
                        className="remove-image-btn"
                        onClick={handleRemoveVideoThumbnail}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                          strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"></path>
                          <line x1="10" y1="11" x2="10" y2="17"></line>
                          <line x1="14" y1="11" x2="14" y2="17"></line>
                        </svg>
                      </button>
                    </div>
                  )}
                  <div
                    className="image-upload-box"
                    onClick={() => document.getElementById('video-thumbnail-input').click()}
                    style={{
                      width: '200px',
                      height: '150px'
                    }}
                  >
                    <span className="plus-icon">+</span>
                  </div>
                </div>
                <input
                  id="video-thumbnail-input"
                  type="file"
                  accept=".png,.jpg,.jpeg,.webp,image/png,image/jpg,image/jpeg,image/webp"
                  onChange={handleVideoThumbnailChange}
                  style={{ display: "none" }}
                />
              </div>

              {/* Video */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.75rem',
                  fontWeight: 700,
                  color: '#475569',
                  fontSize: '0.75rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  textAlign: 'left',
                  width: '100%'
                }}>
                  Video
                </label>
                <div className="image-boxes-container" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  {video && (
                    <div className="image-preview-item" style={{ position: 'relative' }}>
                      <video
                        src={URL.createObjectURL(video)}
                        controls
                        style={{
                          width: '200px',
                          height: '150px',
                          objectFit: 'cover',
                          borderRadius: '12px',
                          border: '2px solid #e2e8f0'
                        }}
                      />
                      <button
                        type="button"
                        className="remove-image-btn"
                        onClick={handleRemoveVideo}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                          strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"></path>
                          <line x1="10" y1="11" x2="10" y2="17"></line>
                          <line x1="14" y1="11" x2="14" y2="17"></line>
                        </svg>
                      </button>
                    </div>
                  )}
                  <div
                    className="image-upload-box"
                    onClick={() => document.getElementById('video-upload-input').click()}
                    style={{
                      width: '200px',
                      height: '150px'
                    }}
                  >
                    <span className="plus-icon">+</span>
                  </div>
                </div>
                <input
                  id="video-upload-input"
                  type="file"
                  accept="video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov"
                  onChange={handleVideoChange}
                  style={{ display: "none" }}
                />
              </div>
            </div>
          </div>

          <div className="submit-btn-row">
            <button
              type="submit"
              className="submit-btn"
              disabled={submitLoading}
            >
              {submitLoading ? "Submitting..." : "Submit"}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default AddProduct; 